import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardTitle, CardDescription, Skeleton } from '@/components/ui';
import { ScoreBadge } from '@/components/keyword';
import { api } from '@/lib/api';
import { formatNumber } from '@keywordgod/shared';
import { cn } from '@/lib/utils';

export function Categories() {
  const list = useQuery({
    queryKey: ['categories', 'list'],
    queryFn: () => api.categories.list(),
    staleTime: 5 * 60 * 1000,
  });

  const [params] = useSearchParams();
  const initial = params.get('cat');
  const [selectedId, setSelectedId] = useState<string | null>(initial);
  // URL ?cat= 변경 시 동기화
  useEffect(() => {
    if (initial && initial !== selectedId) setSelectedId(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <LayoutGrid className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">카테고리</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          {list.data?.categories.length ?? 0}개 분야로 묶인 키워드 — 카드를 누르면 그 분야의 분석
          완료 키워드를 GodScore 순으로 펼쳐 보여드립니다.
        </p>
      </header>

      {list.isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {list.data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.data.categories.map((c) => {
            const active = selectedId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(active ? null : c.id)}
                className={cn(
                  'group rounded-2xl border bg-gray-900 p-4 text-left transition-all',
                  active
                    ? 'border-gold shadow-[0_0_0_3px_rgba(255,215,0,0.15)]'
                    : 'border-gray-800 hover:border-gold/40 hover:bg-gray-900/70',
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{c.emoji}</span>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 transition-transform',
                      active ? 'text-gold rotate-90' : 'text-gray-600 group-hover:text-gold',
                    )}
                  />
                </div>
                <div className="mt-2 text-base font-bold text-white">{c.label}</div>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2">{c.description}</p>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="tabular text-xl font-bold text-gold">
                    {formatNumber(c.totalCount)}
                  </span>
                  <span className="text-[10px] text-gray-500">개 키워드</span>
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  분석 완료 {formatNumber(c.analyzedCount)} · 시드 {c.seedCount}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedId && <CategoryDetail id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

// ─── 선택된 카테고리 상세 ─────────────────────────────────

const MIN_SEARCH_PRESETS = [
  { value: 0, label: '모두' },
  { value: 50, label: '50+' },
  { value: 100, label: '100+' },
  { value: 500, label: '500+' },
  { value: 1000, label: '1K+' },
  { value: 5000, label: '5K+' },
] as const;

function CategoryDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const [minSearch, setMinSearch] = useState<number>(100);
  const [includeUnanalyzed, setIncludeUnanalyzed] = useState<boolean>(true);

  const q = useQuery({
    queryKey: ['categories', 'keywords', id, minSearch, includeUnanalyzed],
    queryFn: () =>
      api.categories.keywords(id, { limit: 60, minSearch, includeUnanalyzed }),
    staleTime: 60 * 1000,
  });

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            {q.data ? (
              <>
                <span className="text-xl">{q.data.category.emoji}</span>
                <span>{q.data.category.label}</span>
                <span className="text-xs font-normal text-gray-500">
                  · 상위 {q.data.items.length}개
                </span>
              </>
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-gold" />
            )}
          </CardTitle>
          {q.data && <CardDescription>{q.data.category.description}</CardDescription>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-gray-400 hover:text-gold"
        >
          닫기
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-800 bg-gray-950/50 p-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 mr-1">최소 월 검색량</span>
          {MIN_SEARCH_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setMinSearch(p.value)}
              className={cn(
                'rounded-md px-2 py-0.5 text-xs font-medium transition-colors',
                minSearch === p.value
                  ? 'bg-gold/15 text-gold'
                  : 'text-gray-400 hover:text-gold',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeUnanalyzed}
            onChange={(e) => setIncludeUnanalyzed(e.target.checked)}
            className="accent-gold"
          />
          미분석 키워드 포함
        </label>
      </div>

      <div className="mt-4">
        {q.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {q.data && q.data.items.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-6">
            조건에 맞는 키워드가 없습니다. 최소 검색량을 낮추거나 "모두" 로 바꿔보세요.
          </p>
        )}

        {q.data && q.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                  <th className="py-2 pr-2 font-medium w-10 text-right">#</th>
                  <th className="py-2 pr-2 font-medium">키워드</th>
                  <th className="py-2 pr-2 font-medium text-right">월 검색량</th>
                  <th className="py-2 pr-2 font-medium text-right">포화도</th>
                  <th className="py-2 pr-2 font-medium text-right">GodScore</th>
                  <th className="py-2 font-medium text-right">등급</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((it, i) => (
                  <tr
                    key={it.keyword}
                    className={cn(
                      'border-b border-gray-900 last:border-b-0 hover:bg-gray-950/60',
                      !it.analyzed && 'opacity-70',
                    )}
                  >
                    <td className="py-2 pr-2 text-right tabular text-xs text-gray-500">{i + 1}</td>
                    <td className="py-2 pr-2 font-medium truncate max-w-[260px]">
                      <Link
                        to={`/analyze/${encodeURIComponent(it.keyword)}`}
                        className={cn(
                          'hover:text-gold inline-flex items-center gap-1.5',
                          it.analyzed ? 'text-white' : 'text-gray-400',
                        )}
                        title={it.analyzed ? '' : '미분석 — 클릭하면 자동 분석'}
                      >
                        <span className="truncate">{it.keyword}</span>
                        {!it.analyzed && (
                          <span className="shrink-0 rounded-full bg-gray-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gray-500">
                            미분석
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="py-2 pr-2 text-right tabular text-gray-300">
                      {it.analyzed ? formatNumber(it.totalSearch) : '-'}
                    </td>
                    <td className="py-2 pr-2 text-right tabular text-gray-400">
                      {it.analyzed ? `${it.saturation}%` : '-'}
                    </td>
                    <td className="py-2 pr-2 text-right tabular font-bold text-gold">
                      {it.analyzed ? it.godScore : '-'}
                    </td>
                    <td className="py-2 text-right">
                      {it.analyzed ? <ScoreBadge grade={it.grade as never} size="sm" /> : <span className="text-[10px] text-gray-600">대기</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
